import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next, once, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { BlockThemeConfig, Service } from 'webapp-types';
import { DataPerformanceSummaryType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { checkToApplyWatchHack } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import safeStyle from 'webapp/helpers/safe-style';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderFetchOverlayComponent from 'webapp/components/bar-loader-fetch-overlay';
import BlockContentComponent from 'webapp/components/block/content';
import CommonPopupsPopupColorsComponent from 'webapp/components/common/popups/popup-colors';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataStatusIconComponent from 'webapp/components/data-status-icon';
import FetchLoaderComponent from 'webapp/components/fetch-loader';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type Block from 'webapp/models-custom/block';
import BlockRenderer from 'webapp/models-custom/block-renderer';
import type BlockState from 'webapp/models-custom/block-state';
import type BoardState from 'webapp/models-custom/board-state';
import type { DataPerformanceSummaryObject } from 'webapp/models-custom/data-performance-summary/block';
import DatawallGrid from 'webapp/models-custom/datawall-grid';
import StatusObject from 'webapp/models-custom/status-object';

import type { onConnectSourceResult } from 'webapp/services/connect';

export interface Args {
	model: Block;
	boardState: BoardState;
	currentZoom: number;
	colorConfig: BlockThemeConfig.Parsed;
	isWallView: boolean;
	skipBlockFetchLoader: boolean;
	skipMasterFilter?: boolean;
	skipActions?: boolean;
	onBlockDelete: () => void;
	onMoveUpOrDown?: (direction: string, block: Block) => void;
	trackingOrigin?: string;
}

export default class BlockWrapperComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked blockRenderer: BlockRenderer | null = null;

	el: JQuery | null = null;

	private bgTheme = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.bgTheme = this.args.colorConfig.bgTheme;

		this.updateSampleHash(true);

		next(() => {
			this.checkToRecreateVisualization();
		});
	}

	get model(): Block {
		return this.args.model;
	}

	@cached
	get blockState(): BlockState | null {
		return this.args.boardState?.getBlockState(this.model) || null;
	}

	get showBlock(): boolean {
		return !!this.blockRenderer; // dont show if too soon
	}

	get idString(): string {
		return 'b' + this.model.id;
	}

	get isDesigner(): boolean {
		return !this.args.boardState.isViewOnly;
	}

	get isUnselectable(): boolean {
		return (
			this.args.boardState.isViewOnly ||
			!!this.model.model.isGenerating ||
			this.model.isFromDataCalculation ||
			this.model.isFromCustomQuery
		);
	}

	get isSelected(): boolean {
		return !this.isUnselectable && this.args.boardState.selectedBlock?.id === this.model.id;
	}

	get isWallView(): boolean {
		return this.args.isWallView;
	}

	get canChangeColor(): boolean {
		return this.currentUserService.allowChangingBlockColor;
	}

	get isBlockFaded() {
		return this.showBlockFetchLoader || this.showConnectButton;
	}

	get canChangeDateRange(): boolean {
		return (
			!this.accountService.isDemoAccount &&
			(!this.args.boardState.isViewOnly || this.args.boardState.allowDateRangePreviews)
		);
	}

	get canChangeFilter(): boolean {
		const { boardCM, isViewOnly, isReportEditor, allowFilterPreviews } = this.args.boardState;
		const isViewOnlyRandomizedBoard = boardCM.isRandom && isViewOnly && !isReportEditor; // account and public templates

		return (
			!this.accountService.isDemoAccount &&
			!isViewOnlyRandomizedBoard &&
			(!isViewOnly || allowFilterPreviews)
		);
	}

	get canChangeVisualization(): boolean {
		return !this.accountService.isDemoAccount && !this.navigationService.isMobileApp;
	}

	get showSelectedBlockActions() {
		return !this.model.model.isGenerating;
	}

	get showBlockFetchLoader() {
		return (
			!this.args.skipBlockFetchLoader &&
			!this.model.model.isGenerating &&
			!!this.blockState?.isLoadingData &&
			!this.model.isFeatureLimited
		);
	}

	get colorConfig() {
		return this.args.colorConfig;
	}

	get fetchLoaderColorPairs() {
		return this.model.boardObj?.themeMainColorPair || [];
	}

	@cached
	get showFavoriteMetricBtn(): boolean {
		let showBtn = false;
		if (
			this.mmMetricsService.showFavoriteAction &&
			this.isDesigner &&
			!this.model.isAnyAdvancedSetupSelected
		) {
			showBtn = this.model.metricDataArray.length > 0;
		}
		return showBtn;
	}

	@cached
	get canAnnotate(): boolean {
		const boardObj = this.model.boardObj;
		return (
			!!boardObj &&
			boardObj.canCurrentUserEdit &&
			!boardObj.isRandom &&
			(!this.navigationService.spaceIdOfDatawall ||
				this.navigationService.spaceIdOfDatawall === this.accountService.id)
		);
	}

	get dataPerformanceSummaryObject() {
		if (this.model.showDataPerformanceSummary) {
			const summaryObj = {
				objectType: DataPerformanceSummaryType.BLOCK,
				objectId: this.model.id || '',
				previewHash: '',
			} as DataPerformanceSummaryObject;

			if (this.blockState?.block.isPreview) {
				summaryObj.previewHash = this.blockState?.block.previewHash;
			}

			return summaryObj;
		}

		return null;
	}

	private _sampleHash = 0;

	get sampleHash() {
		this.updateSampleHash();
		return this._sampleHash;
	}

	private updateSampleHash(isOnInit = false) {
		// set the correct hash on init, so we don't immediately trigger rerenderWithDataChange
		// but do not call the event handler on init
		const sampleData = this.blockState?.shownData,
			newSampleHash = hashCode(JSON.stringify(sampleData) || '');
		if (this._sampleHash !== newSampleHash) {
			this._sampleHash = newSampleHash;
			if (newSampleHash) {
				if (!isOnInit) {
					checkToApplyWatchHack('block/wrapper:rerenderWithDataChange', () => {
						this.rerenderWithDataChange();
					});
				}
			}
		}
	}

	// --------- Block-wide "connect" ---------

	get showConnectButton() {
		if (this.navigationService.isLoadedFromSnapshooter) {
			return false;
		}
		if (!this.isDesigner) {
			return false;
		}
		const board = this.model.boardObj;
		if (!board || board.isRandom) {
			return false;
		}
		if (!this.model.isAllUsedMetricsGeneric) {
			return false;
		}
		if (!this.sourceTypeToConnect) {
			return false;
		}
		return this.model.missingSourceTypeIds.length === 1;
	}

	@cached
	get sourceTypeToConnect() {
		const sourceTypeId = +this.model.missingSourceTypeIds[0]!;
		return this.mmSourceTypesService.findModelById(sourceTypeId);
	}

	get isActionConnect() {
		// must not have any source of this type
		return !this.sourceTypeToConnect?.connectedSources.length;
	}

	get connectButtonText() {
		return this.isActionConnect ? 'Connect' : 'Select';
	}

	@action
	connect() {
		if (this.isActionConnect) {
			const sourceType = this.sourceTypeToConnect;
			if (sourceType) {
				void sourceType
					.connectNew({ origin: 'datablock' })
					.then((result: onConnectSourceResult) => {
						if (result.isSuccess && result.source.id) {
							// after the source metrics are added to store
							next(() => {
								void this.model.replaceGenericMetrics(
									sourceType.id!,
									String(result.source.id),
								);
							});
						}
					});
			}
		} else {
			this.mmBlocksService.openResolveDataPopup(this.model);
		}
	}

	// --------- Sizing ---------

	@tracked boardSizeCoeff = 1;
	@tracked fontSize = 10;
	@tracked baseFontSize = 10; // font size before user modifier is applied
	get isBlockFragmentInReport(): boolean {
		if (!this.el) {
			return false;
		}

		const isOnReport = this.model.isFromReport,
			// fragment--block includes metric and ai-summary fragments
			hasFragmentBlock = this.el.closest('.fragment--block').length > 0;

		return isOnReport && hasFragmentBlock;
	}

	get blockSizeCoeff(): number {
		let maxWidthUsed, maxHeightUsed, blockSizeCoeff;

		let modelWidth = this.model.sizes.width || DatawallGrid.wallWidth,
			modelHeight = this.model.sizes.height || DatawallGrid.wallHeight;

		if (this.isBlockFragmentInReport) {
			const el = this.el!;

			const pagelessContainer = el.closest('.pageless-content, .usercontent');
			const containerWidth = pagelessContainer.width()!,
				calculatedSlideHeight = (containerWidth * 9) / 16;

			const blockWidth = el.width()!,
				blockHeight = el.height()!;

			modelWidth = DatawallGrid.calculateReportBlockWidthFromSlideWidth(
				containerWidth,
				blockWidth,
			);
			modelHeight = DatawallGrid.calculateReportBlockHeightFromSlideHeight(
				calculatedSlideHeight,
				blockHeight,
			);
		}

		if (this.model.isNumber) {
			maxWidthUsed = DatawallGrid.wallWidth;
			maxHeightUsed = DatawallGrid.wallHeight;
		} else if (this.model.isText) {
			maxWidthUsed = DatawallGrid.minimumSize;
			maxHeightUsed = DatawallGrid.minimumSize;
		} else {
			maxWidthUsed = DatawallGrid.wallWidth / 2;
			maxHeightUsed = (3 * DatawallGrid.wallHeight) / 4;
		}

		blockSizeCoeff = Math.min(maxHeightUsed, modelWidth) * Math.min(maxWidthUsed, modelHeight);

		blockSizeCoeff = blockSizeCoeff / (2 * DatawallGrid.minimumSize);

		if (blockSizeCoeff >= 4 && blockSizeCoeff < 8) {
			blockSizeCoeff *= 1.3;
		}

		// set minimum blockSizeCoeff for Number blocks
		if (this.model.isNumber) {
			blockSizeCoeff = Math.max(blockSizeCoeff, 6);
		}

		return blockSizeCoeff;
	}

	get minFontSizePx(): number {
		let absoluteMinFontSizePx;

		if (this.model.isText) {
			absoluteMinFontSizePx = 6;
		} else {
			absoluteMinFontSizePx = 1;
		}

		const minFontSizePx = Math.max(absoluteMinFontSizePx, 4 * this.boardSizeCoeff);

		return minFontSizePx;
	}

	get maxFontSizePx(): number {
		let maxFontSizePx;

		const absoluteMaxFontSizePx = 40;
		if (this.model.isText) {
			maxFontSizePx = this.minFontSizePx;
		} else {
			maxFontSizePx = Math.min(absoluteMaxFontSizePx, 10 * this.boardSizeCoeff);
		}

		return maxFontSizePx;
	}

	get bgColor() {
		return (
			this.model.color ||
			this.model.board?.color ||
			this.colorConfig.bgColor ||
			'var(--body-background-3)'
		);
	}

	get safeStyle() {
		const styles = [
			`font-size:${this.fontSize}px;`,
			`--block-bg-color:${this.bgColor};`,
			`--block-base-font-size:${this.baseFontSize}px;`,
		];
		return htmlSafe(styles.join(''));
	}

	getReferenceFontSize(): {
		fontSize: number;
		referenceWidth: number;
		referenceHeight: number;
	} {
		if (this.model.isDataStoryOnSlide) {
			// reference taken from previous version on production - present on 1920x1080px screen (snapshooter default)
			return { fontSize: 15.1, referenceWidth: 1920, referenceHeight: 1080 };
		} else {
			return { fontSize: 16, referenceWidth: 800, referenceHeight: 600 };
		}
	}

	calculateLinearFontSize(targetSelector?: string): number {
		const el = this.el;
		if (!el) return 10;

		// if no selector provided, use the block itself; otherwise find the target container
		const target = targetSelector ? el.closest(targetSelector) : null;
		const elementToMeasure = target?.length ? target : el;

		const {
			fontSize: referenceFontSize,
			referenceWidth,
			referenceHeight,
		} = this.getReferenceFontSize();

		// use area-based scaling for better handling of independently resizable dimensions (blocks on boards)
		const currentWidth = elementToMeasure.outerWidth()!;
		const currentHeight = elementToMeasure.outerHeight()!;

		const currentArea = currentWidth * currentHeight;
		const referenceArea = referenceWidth * referenceHeight;

		// use square root since font size scales linearly, not quadratically with area
		const scaleFactor = Math.sqrt(currentArea / referenceArea);

		return referenceFontSize * scaleFactor;
	}

	updateBoardSizeCoeff(): void {
		let boardSizeCoeff,
			oneBlockWidth = 0;
		const oneBlockWidthFallback = $(window).width()! / 10;

		try {
			const el = this.el;

			if (!el) {
				return;
			}

			const blocksEl = el.closest('.view-board-content-inner');
			const divider = DatawallGrid.wallWidth / DatawallGrid.minimumSize;
			if (blocksEl.length) {
				oneBlockWidth = blocksEl.width()! / divider;
			} else {
				const singleBlockEl = el.closest('.view-single-block');
				if (singleBlockEl.length) {
					if (this.model.isFromReport) {
						let reportContainerEl = el.closest('.report_slide_inner');
						if (!reportContainerEl.length) {
							reportContainerEl = el.closest('.pageless-content');
						}
						oneBlockWidth = reportContainerEl.width()! / divider;
					} else if (this.model.isFromCustomQuery || this.model.isFromDataCalculation) {
						// singleBlockEl has width bcos of parent
						const customDivider = 3; // some arbitrary number to keep approx same fontsize as legacy
						oneBlockWidth = singleBlockEl.width()! / customDivider;
					} else if (this.model.isShownInBlockPreview) {
						const popupContentEl = el.closest('.chart-container');

						if (popupContentEl.length) {
							oneBlockWidth = popupContentEl.width()! / divider;
						}
					} else {
						// metric details - epxlore/forecast
						oneBlockWidth = 150;
					}
				} else {
					oneBlockWidth = 150;
				}
			}
		} catch (_e) {
			// should not come to this really, but just in case...
			oneBlockWidth = oneBlockWidthFallback;
		}

		if (!oneBlockWidth) {
			oneBlockWidth = oneBlockWidthFallback;
		}

		// assume that for 100px boardSizeCoeff is 1
		boardSizeCoeff = oneBlockWidth / 100;

		if (
			this.model.height === 2 * DatawallGrid.minimumSize &&
			this.model.width === 2 * DatawallGrid.minimumSize &&
			!this.model.isShownInBlockPreview // we take care that blocks on preview are always same size (check getter blockSizeCoeff)
		) {
			boardSizeCoeff += 0.5;
		}

		this.boardSizeCoeff = boardSizeCoeff;
	}

	updateStretchStyle(): boolean {
		let calculatedFontSize = 10;

		if (this.isWallView || this.model.isFromReport) {
			if (this.model.isDataStoryOnSlide) {
				calculatedFontSize = this.calculateLinearFontSize('.report_slide_inner');
				// skip min/max limits for linear scaling to allow true proportional scaling
			} else {
				this.updateBoardSizeCoeff();

				const blockSizeCoeff = this.blockSizeCoeff,
					boardSizeCoeff = this.boardSizeCoeff,
					minFontSizePx = this.minFontSizePx,
					maxFontSizePx = this.maxFontSizePx;

				calculatedFontSize = Math.max(
					minFontSizePx,
					Math.min(maxFontSizePx, boardSizeCoeff * blockSizeCoeff),
				);
			}
		}

		const coefficient = this.mmBlocksService.getModifierFromFontSize(this.model.fontSize);
		const fontSize = calculatedFontSize * coefficient;

		const fontSizeChanged = this.fontSize !== fontSize;
		const baseFontSizeChanged = this.baseFontSize !== calculatedFontSize;

		if (baseFontSizeChanged) {
			this.baseFontSize = calculatedFontSize;
			// set the fontsize on element immediately bcos blockRenderer reads it
			const node = this.el?.get(0);
			if (node) {
				node.style.setProperty('--block-base-font-size', `${calculatedFontSize}px`);
			}
		}

		if (fontSizeChanged) {
			this.fontSize = fontSize;
			// set the fontsize on element immediately bcos blockRenderer reads it
			const node = this.el?.get(0);
			if (node) {
				node.style.fontSize = `${fontSize}px`;
			}
		}

		return fontSizeChanged || baseFontSizeChanged;
	}

	// --------- Status ---------

	get statusObjects() {
		return this.model.statusObjects;
	}

	get lastDataSync() {
		return this.model.lastSync || null;
	}

	get showDataStatusIcon(): boolean {
		return (
			!this.navigationService.isLoadedFromSnapshooter &&
			(!!this.lastDataSync || this.dataErrorStatusObjects.length > 0) &&
			!this.accountService.isDemoAccount &&
			!this.model.isAiSummary
		);
	}

	get dataErrorStatusObjects(): StatusObject[] {
		return StatusObject.eliminateDuplicates(
			this.statusObjects.filter((item) => !item.isSync && !item.isMediaSync),
			true,
		);
	}

	get dataErrorStatusObjectLabels(): Map<StatusObject, string> {
		return StatusObject.getDatasetMetricLabels(
			this.dataErrorStatusObjects,
			this.model.blockSetup.dataSelects.map((ds) => ({
				statusObjects: ds.statusObjects,
				datasetMetricLabel: ds.isSourceDataset
					? StatusObject.getDatasetMetricLabel(
							ds.metricName || ds.dataRoot?.metricName,
							ds.metricSetup.source?.title || ds.dataRoot?.source?.name,
						)
					: '',
			})),
		);
	}

	get showGenerativeAiInfo() {
		return this.model.isAiSummary;
	}

	// ---------- Visualization stuff -----------

	@action
	onVisualizationChange(): void {
		this.checkToRecreateVisualization();
		this.rerender();
	}

	private _previousVisualizationSettingsHash = 0;

	@action
	onVisualizationSettingsHashChange(): void {
		const blockRenderer = this.blockRenderer;
		if (!blockRenderer) {
			return;
		}

		if (this._previousVisualizationSettingsHash === blockRenderer.visualizationSettingsHash) {
			return;
		}

		this._previousVisualizationSettingsHash = blockRenderer.visualizationSettingsHash;
		once(this, this.rerender);
	}

	private checkToRecreateVisualization(): void {
		if (
			this.model.blockSetup.visualizationType !==
			this.model.blockSetup.visualization.visualizationType
		) {
			this.model.blockSetup.recreateVisualization();
		}
	}

	// ---------- Render Methods -----------

	private rerender(): void {
		// generic "check if anything changed"
		void this.blockRenderer?.rerender();
	}

	private rerenderWithDataChange() {
		void this.blockRenderer?.rerender(false, true);
	}

	private _previousBlockWidth = 0;
	private _previousBlockHeight = 0;

	private rerenderWithSizeChange(skipChangeCheck: boolean) {
		// we skip the change check on actual resize (e.g. metric library where blocksize and fontSize do not change)
		const fontSizeChanged = this.updateStretchStyle(),
			blockWidthChanged = this.model.width !== this._previousBlockWidth,
			blockHeightChanged = this.model.height !== this._previousBlockHeight;

		if (!skipChangeCheck && !fontSizeChanged && !blockWidthChanged && !blockHeightChanged) {
			return;
		}

		this._previousBlockWidth = this.model.width;
		this._previousBlockHeight = this.model.height;

		this.rerender();
	}

	private _previousTitle = this.model.name;

	@action
	onTitleChange(): void {
		// sometimes triggers without actual title change - check if needed
		if (this._previousTitle === this.model.name) {
			return;
		}
		this._previousTitle = this.model.name;
		this.rerender();
	}

	@action
	onBlockSizeChange(): void {
		// this can trigger multiple times on block resize, hence debounce
		debounce(this, this.rerenderWithSizeChange, false, 250);
	}

	@action
	onResize(): void {
		debounce(this, this.rerenderWithSizeChange, true, 250);
	}

	@action
	onFontLoad(): void {
		void this.blockRenderer?.rerender(true);
	}

	@action
	onBgThemeChange(): void {
		// the white theme has borders, so we need to recalculate available space
		if (this.bgTheme !== this.args.colorConfig.bgTheme) {
			this.bgTheme = this.args.colorConfig.bgTheme;
			this.rerender();
		}
	}

	@action
	initBlockRenderer(onFirstSetup: boolean): void {
		// destroy previous
		if (this.blockRenderer) {
			this.blockRenderer.destroy();
		}

		const el = this.el;

		if (!el) {
			return;
		}

		// create new
		const blockRenderer = new BlockRenderer(this.model, el.get(0)!, this.args.isWallView);
		this.blockRenderer = blockRenderer;

		const hasData = this.sampleHash !== 0;
		if (hasData) {
			blockRenderer.updateVisualizationDataCache();
		}

		if (onFirstSetup) {
			schedule('afterRender', () => {
				this.rerender();
			});
		} else {
			this.rerender();
		}

		this._previousVisualizationSettingsHash = blockRenderer.visualizationSettingsHash;
		this._previousBlockWidth = this.model.width;
		this._previousBlockHeight = this.model.height;
	}

	// --------- Actions -----------

	@action
	blockSelect(): boolean {
		if (!this.isUnselectable && !this.isSelected) {
			this.eventManagerService.trigger(AppEvent.BLOCK_SELECT);
			this.navigationService.transitionToRoute(
				'designer.board.block',
				this.model.board,
				this.model.model,
			);
		}
		return true;
	}

	@action
	copyBlock(): void {
		this.mmBlocksService.openDuplicatePopup({
			block: this.model,
			trackingOrigin: 'designer',
		});
	}

	@action
	favoriteMetric(): void {
		// Only shown while mmMetricsService.showFavoriteAction is on; favoriting moves onto the registry in M2.
	}

	@action
	changeColor(): void {
		const isPopupOpen = this.popupService.isOpen(CommonPopupsPopupColorsComponent);

		if (!isPopupOpen) {
			const attachTo = this.el?.find('.active-block span.color').get(0);

			if (!attachTo) {
				return;
			}

			const popup = this.popupService.createContext(
				CommonPopupsPopupColorsComponent,
				attachTo,
				{
					offset: 0,
					popupClass: 'popup-type-color popup-type-color--block',
					theme: 'dark',
				},
			);

			popup.open({
				selected: this.model.color,
				allowEmpty: true,
				changeColor: (color: string | number) => {
					if (this.model && this.model.color !== color) {
						this.model.color = color as string;
						void this.model.save();
					}
				},
			});
		}
	}

	@action
	openEnableCustomDataUpdatePopup() {
		const board = this.model.boardObj;

		if (!board) {
			return;
		}

		this.mmBoardsService.openEnableCustomDataUpdatePopup(board, this.args.trackingOrigin);
	}

	@action
	onMoveUpOrDown(direction: string, block: Block) {
		if (this.args.onMoveUpOrDown) {
			this.args.onMoveUpOrDown(direction, block);
		}
	}

	// -------- Lifecycle stuff ---------
	@action
	setupView(node: HTMLElement): void {
		const el = $(node);
		this.el = el;

		this.updateStretchStyle();
		this.initBlockRenderer(true);

		if (!this.isUnselectable) {
			this.setupScrollTo(el);
		}

		this.el.on('resize.block', () => {
			this.onResize();
		});
	}

	@action
	teardownView(): void {
		if (this.blockRenderer) {
			this.blockRenderer.destroy();
			this.blockRenderer = null;
		}
	}

	private setupScrollTo(el: JQuery): void {
		const link = el.find('.block-link');

		link.on('block.select', () => {
			const scroll = link.closest('.scroll'),
				linkTopOffset = (link.offset() as { top: number }).top,
				scrollTopOffset = (scroll.offset() as { top: number }).top,
				scrollTop = scroll.scrollTop()!,
				scrollOffset = scroll.find('.scroll-offset').outerHeight(true)!;

			let offset = Math.floor(linkTopOffset - scrollTopOffset + scrollTop - scrollOffset);

			if (offset > scrollOffset) {
				offset += scrollOffset;
			}

			// WEBKIT FIX
			if (offset > 0) {
				offset--;
			}

			scroll.animate(
				{
					scrollTop: offset,
				},
				500,
			);
		});
	}
	<template>
		<div
			class="preview-block visualization--{{this.model.visualization}}"
			data-sample-v={{this.sampleHash}}
			data-clientrender-v={{this.blockRenderer.visualizationSettingsHash}}
			data-order={{this.model.order}}
			data-localid={{this.model.localId}}
			style={{this.safeStyle}}
			{{didInsert this.setupView}}
			{{didUpdate (fn this.initBlockRenderer false) this.model @isWallView}}
			{{didUpdate this.onFontLoad this.navigationService.isFontLoaded}}
			{{didUpdate this.onTitleChange @model.name}}
			{{didUpdate
				this.onBlockSizeChange
				this.model.width
				this.model.height
				this.model.fontSize
				this.blockRenderer.blockInnerWidth
				this.blockRenderer.blockInnerHeight
				@currentZoom
			}}
			{{didUpdate this.onBgThemeChange @colorConfig}}
			{{didUpdate this.onVisualizationChange this.model.visualization}}
			{{didUpdate
				this.onVisualizationSettingsHashChange
				this.blockRenderer.visualizationSettingsHash
			}}
			{{willDestroy this.teardownView}}
		>
			<div
				role="link"
				class="block-link
					{{if this.isUnselectable 'disabled'}}
					{{if this.isSelected 'active'}}"
				style={{this.model.colorGradient}}
				{{on "click" this.blockSelect}}
			>

				<div
					id={{this.idString}}
					class="block
						{{if this.model.model.isGenerating 'temporary'}}
						{{if this.isBlockFaded 'faded'}}"
				>
					{{#if this.model.model.isGenerating}}
						<BarLoaderFetchOverlayComponent @text="Generating datablock..." />
					{{else}}
						{{#if this.blockState}}
							{{#if this.blockRenderer}}
								<BlockContentComponent
									@blockState={{this.blockState}}
									@blockRenderer={{this.blockRenderer}}
									@isWallView={{this.isWallView}}
									@canEdit={{this.isSelected}}
									@canChangeDateRange={{this.canChangeDateRange}}
									@canChangeFilter={{this.canChangeFilter}}
									@canChangeVisualization={{this.canChangeVisualization}}
									@canAnnotate={{this.canAnnotate}}
									@isUnselectable={{this.isUnselectable}}
									@skipMasterFilter={{@skipMasterFilter}}
									@colorConfig={{this.colorConfig}}
									@dataPerformanceSummaryObject={{this.dataPerformanceSummaryObject}}
								/>
							{{/if}}
						{{/if}}

					{{/if}}
				</div>

				{{#if this.isWallView}}
					<small class="resize-dot dot-n"></small>
					<small class="resize-dot dot-ne"></small>
					<small class="resize-dot dot-e"></small>
					<small class="resize-dot dot-se"></small>
					<small class="resize-dot dot-s"></small>
					<small class="resize-dot dot-sw"></small>
					<small class="resize-dot dot-w"></small>
					<small class="resize-dot dot-nw"></small>
				{{/if}}

				{{#if this.showBlockFetchLoader}}
					<FetchLoaderComponent @colors={{this.fetchLoaderColorPairs}} @type="block" />
				{{else if this.showConnectButton}}
					<SourceConnectButtonComponent
						@sourceType={{this.sourceTypeToConnect}}
						@onConnect={{this.connect}}
						@text={{this.connectButtonText}}
						@theme="green"
					/>
				{{else if this.showDataStatusIcon}}
					<DataStatusIconComponent
						@context="block"
						@lastSync={{this.lastDataSync}}
						@statusObjects={{this.dataErrorStatusObjects}}
						@statusObjectLabels={{this.dataErrorStatusObjectLabels}}
						@skipActions={{@skipActions}}
						@theme={{this.colorConfig.bgTheme}}
						@openEnableCustomDataUpdatePopup={{if
							this.model.boardObj
							this.openEnableCustomDataUpdatePopup
						}}
						@iconSize="dynamic"
					/>
				{{/if}}

				{{#if this.showSelectedBlockActions}}
					{{! template-lint-disable no-nested-interactive }}
					<div class="active-block">
						<small>{{this.model.name}}</small>
						{{#unless this.isWallView}}
							<div class="active-block__mobile-actions">
								<span
									role="button"
									class="play-up"
									{{on "click" (fn this.onMoveUpOrDown "up" this.model)}}
								>
									<CommonUiIconComponent @name="PlayFill" @size="dynamic" />
								</span>
								<span
									role="button"
									class="play-down"
									{{on "click" (fn this.onMoveUpOrDown "down" this.model)}}
								>
									<CommonUiIconComponent @name="PlayFill" @size="dynamic" />
								</span>
							</div>
						{{/unless}}
						<span role="button" class="del" {{on "click" @onBlockDelete}}>
							<CommonUiIconComponent @name="Trash" @size="dynamic" />
							<ToolTipComponent @text="Delete datablock" />
						</span>
						<span role="button" class="copy" {{on "click" this.copyBlock}}>
							<CommonUiIconComponent @name="Copy" @size="dynamic" />
							<ToolTipComponent @text="Duplicate datablock" />
						</span>
						{{#if this.showFavoriteMetricBtn}}
							<span role="button" class="favorite" {{on "click" this.favoriteMetric}}>
								<CommonUiIconComponent @name="StarFill" @size="dynamic" />
								<ToolTipComponent @text="Favorite metric" />
							</span>
						{{/if}}
						{{#if this.canChangeColor}}
							<span role="button" class="color" {{on "click" this.changeColor}}>
								<CommonUiIconComponent
									@name={{if this.model.color "Raindrop" "RaindropAuto"}}
									@size="dynamic"
									style={{safeStyle this.model.color property="fill"}}
								/>
								<ToolTipComponent @text="Change color" />
							</span>
						{{/if}}
					</div>
				{{/if}}

				{{!-- <Block::DebugButton @blockId={{@model.numericId}} /> --}}

			</div>
		</div>
	</template>
}
